import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { propertiesAPI } from '../services/api';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons by status
const createCustomIcon = (color) => {
  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

const icons = {
  'Disponível': createCustomIcon('green'),
  'Em Negociação': createCustomIcon('yellow'),
  'Em Assinatura do Contrato': createCustomIcon('violet'),
  'Contrato Assinado': createCustomIcon('blue'),
  'Em Licenciamento': createCustomIcon('orange'),
  'Licença Emitida': createCustomIcon('gold'),
  'Reservado': createCustomIcon('grey'),
  'Locado': createCustomIcon('black'),
  'Indisponível': createCustomIcon('red')
};

function MapView() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    availabilityStatus: '',
    propertyType: ''
  });

  useEffect(() => {
    loadProperties();
  }, [filters]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const response = await propertiesAPI.getForMap(filters);
      setProperties(response.data);
    } catch (error) {
      toast.error('Erro ao carregar imóveis para o mapa');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Disponível': '#22c55e',           // Verde vibrante
      'Em Negociação': '#eab308',         // Amarelo/Dourado
      'Em Assinatura do Contrato': '#8b5cf6', // Roxo/Violeta
      'Contrato Assinado': '#3b82f6',     // Azul
      'Em Licenciamento': '#f97316',       // Laranja
      'Licença Emitida': '#14b8a6',       // Turquesa/Teal
      'Reservado': '#6b7280',             // Cinza
      'Locado': '#1e293b',                // Azul escuro/Navy
      'Indisponível': '#ef4444'           // Vermelho
    };
    return colors[status] || '#a0aec0';
  };

  // Calculate map center based on properties
  const getMapCenter = () => {
    if (properties.length === 0) {
      return [-15.7801, -47.9292]; // Brasília as default center
    }
    
    const lats = properties.map(p => p.location.coordinates[1]);
    const lngs = properties.map(p => p.location.coordinates[0]);
    
    return [
      (Math.min(...lats) + Math.max(...lats)) / 2,
      (Math.min(...lngs) + Math.max(...lngs)) / 2
    ];
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Mapa de Imóveis</h1>
          <p className="page-subtitle">Visualize todos os imóveis cadastrados no mapa</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: '20px' }}>
        <select
          className="form-select filter-select"
          value={filters.availabilityStatus}
          onChange={(e) => setFilters(prev => ({ ...prev, availabilityStatus: e.target.value }))}
        >
          <option value="">Todos os status</option>
          <option value="Disponível">Disponível</option>
          <option value="Em Negociação">Em Negociação</option>
          <option value="Em Assinatura do Contrato">Em Assinatura do Contrato</option>
          <option value="Contrato Assinado">Contrato Assinado</option>
          <option value="Em Licenciamento">Em Licenciamento</option>
          <option value="Licença Emitida">Licença Emitida</option>
          <option value="Reservado">Reservado</option>
          <option value="Locado">Locado</option>
          <option value="Indisponível">Indisponível</option>
        </select>
        <select
          className="form-select filter-select"
          value={filters.propertyType}
          onChange={(e) => setFilters(prev => ({ ...prev, propertyType: e.target.value }))}
        >
          <option value="">Todos os tipos</option>
          <option value="Terreno">Terreno</option>
          <option value="Cobertura">Cobertura</option>
          <option value="Laje">Laje</option>
          <option value="Telhado">Telhado</option>
          <option value="Torre Existente">Torre Existente</option>
          <option value="Outro">Outro</option>
        </select>
        <span style={{ fontSize: '14px', color: 'var(--gray-600)', marginLeft: 'auto' }}>
          📍 {properties.length} imóvel(is) no mapa
        </span>
      </div>

      {/* Legend */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body" style={{ padding: '12px 20px' }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--gray-700)', display: 'block', marginBottom: '12px' }}>Legenda:</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 24px' }}>
            {[
              'Disponível',
              'Em Negociação',
              'Em Assinatura do Contrato',
              'Contrato Assinado',
              'Em Licenciamento',
              'Licença Emitida',
              'Reservado',
              'Locado',
              'Indisponível'
            ].map((status) => (
              <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '14px', 
                  height: '14px', 
                  borderRadius: '50%', 
                  background: getStatusColor(status),
                  flexShrink: 0,
                  border: status === 'Locado' ? 'none' : '1px solid rgba(0,0,0,0.1)'
                }} />
                <span style={{ fontSize: '12px', color: 'var(--gray-600)' }}>{status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading-spinner" style={{ height: '600px' }}>
              <div className="spinner"></div>
            </div>
          ) : (
            <div style={{ height: '600px' }}>
              <MapContainer 
                center={getMapCenter()} 
                zoom={5} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {properties.map((property) => (
                  <Marker
                    key={property._id}
                    position={[property.location.coordinates[1], property.location.coordinates[0]]}
                    icon={icons[property.availabilityStatus] || icons['Indisponível']}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--gray-900)' }}>
                          {property.propertyName || 'Imóvel sem nome'}
                        </h4>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: 'var(--gray-600)' }}>
                          📍 {property.address?.city}/{property.address?.state}
                        </p>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: 'var(--gray-600)' }}>
                          🏢 {property.propertyType}
                        </p>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: 'var(--gray-600)' }}>
                          📐 Área: {property.availableArea}
                        </p>
                        <p style={{ margin: '0 0 12px 0' }}>
                          <span 
                            style={{ 
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              background: getStatusColor(property.availabilityStatus),
                              color: 'white'
                            }}
                          >
                            {property.availabilityStatus}
                          </span>
                        </p>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--gray-500)' }}>
                          Proprietário: {property.owner?.name}
                        </p>
                        <Link 
                          to={`/imoveis/${property._id}`}
                          style={{ 
                            fontSize: '13px', 
                            color: 'var(--primary)', 
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                        >
                          Ver detalhes →
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </div>
      </div>

      {properties.length === 0 && !loading && (
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="empty-state">
            <div className="empty-state-icon">🗺️</div>
            <div className="empty-state-title">Nenhum imóvel encontrado</div>
            <div className="empty-state-text">
              {filters.availabilityStatus || filters.propertyType
                ? 'Tente ajustar os filtros para ver mais imóveis'
                : 'Cadastre imóveis com localização para visualizá-los no mapa'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MapView;
